import { createEffect, createMemo, createResource, ErrorBoundary, For, on, onCleanup, onMount, Show, Suspense } from "solid-js"
import { createStore } from "solid-js/store"
import { useLanguage } from "@/context/language"
import {
  deleteApprovalReviewer, deleteArtifact, deleteFile, getPeriodWorkflowPipeline, getWorkflowRunPipeline, requestApprovals, updateApprovalReviewer,
  type CoworkFile, type CoworkPortfolioWorkflow, type CoworkWorkflowRun,
} from "@/cowork/api"
import { shouldRefreshCoworkFiles } from "@/cowork/file-events"
import { ApprovalTab, FilesTab, OutputTab, RecordsTab, ReportTab } from "./session/v2/cowork-files-panel"

export const workflowPipelineTabs = ["files", "extractions", "transformations", "output", "report", "approval"] as const
export type WorkflowPipelineTab = typeof workflowPipelineTabs[number]
export function workflowPipelineTab(value: unknown): WorkflowPipelineTab {
  if (value === "reports") return "report"
  return workflowPipelineTabs.includes(value as WorkflowPipelineTab) ? value as WorkflowPipelineTab : "files"
}

export function WorkflowPipelineTabs(props: { tab: string; onSelect: (tab: WorkflowPipelineTab) => void }) {
  const language = useLanguage()
  return (
    <nav class="flex shrink-0 gap-1 overflow-x-auto border-b border-v2-border-border-base py-2" aria-label={language.t("cowork.enterprise.workflows.reporting.results")}>
      <For each={workflowPipelineTabs}>{(tab) => (
        <button type="button" class="shrink-0 rounded-md px-4 py-2 text-[13px] hover:bg-v2-overlay-simple-overlay-hover"
          classList={{ "bg-v2-background-bg-layer-03 font-medium": props.tab === tab }}
          aria-current={props.tab === tab ? "page" : undefined} onClick={() => props.onSelect(tab)}>
          {language.t(`cowork.pipeline.${tab}`)}
        </button>
      )}</For>
    </nav>
  )
}

export function CoworkWorkflowPipeline(props: {
  workflow: Pick<CoworkPortfolioWorkflow, "id" | "company_id" | "project_id" | "name">
  periodWorkflow?: boolean
  run?: CoworkWorkflowRun
  tab: WorkflowPipelineTab
  configuredFiles?: CoworkFile[]
  periodID?: string | null
  onFilesChanged?: () => unknown
  loading?: boolean
  error?: boolean
}) {
  const language = useLanguage()
  const [state, setState] = createStore({ filter: "", selectedFile: "", mutationError: false })
  let disposed = false
  onCleanup(() => { disposed = true })
  const identity = () => JSON.stringify([
    props.periodWorkflow ? "period" : "run", props.workflow.company_id, props.workflow.project_id,
    props.workflow.id, props.periodWorkflow ? props.periodID : props.run?.id,
  ])
  const scopeKey = createMemo(() => {
    if (props.periodWorkflow) return props.periodID ? identity() : undefined
    const run = props.run
    return run ? JSON.stringify([...JSON.parse(identity()), run.updated_at, run.status]) : undefined
  })
  const [data, actions] = createResource(scopeKey, async (key) => {
    const [kind, companyID, projectID, workflowID, selectedID] = JSON.parse(key) as [string, string, string, string, string]
    const expected = JSON.stringify([kind, companyID, projectID, workflowID, selectedID])
    if (kind === "period") {
      const value = await getPeriodWorkflowPipeline(projectID, selectedID, companyID)
      if (value.company_id !== companyID || value.project_id !== projectID || value.period_id !== selectedID) throw new Error("Workflow scope mismatch")
      return { scope: expected, value }
    }
    const value = await getWorkflowRunPipeline(projectID, workflowID, selectedID, companyID)
    if (value.company_id !== companyID || value.project_id !== projectID || value.workflow_id !== workflowID || value.run_id !== selectedID) throw new Error("Workflow scope mismatch")
    return { scope: expected, value }
  })
  const currentData = () => {
    if (data.error) return undefined
    const value = data.latest
    return value?.scope === identity() ? value.value : undefined
  }
  const hasScope = () => !!props.run || !!(props.periodWorkflow && props.periodID)
  createEffect(on(identity, () => setState({ filter: "", selectedFile: "", mutationError: false })))
  const refresh = () => { if (scopeKey()) void Promise.resolve(actions.refetch()).catch(() => undefined) }
  onMount(() => {
    const uploaded = (event: Event) => {
      const run = props.run
      if (!hasScope()) return
      if (shouldRefreshCoworkFiles((event as CustomEvent).detail, {
        company_id: props.workflow.company_id, project_id: props.workflow.project_id,
        period_id: props.periodWorkflow ? props.periodID : run?.period_id,
      }, run?.session_remote_id || "")) refresh()
    }
    window.addEventListener("focus", refresh)
    window.addEventListener("cowork-files-uploaded", uploaded)
    onCleanup(() => {
      window.removeEventListener("focus", refresh)
      window.removeEventListener("cowork-files-uploaded", uploaded)
    })
  })
  const files = () => hasScope() ? currentData()?.files ?? [] : (props.configuredFiles ?? []).filter((file) => file.company_id === props.workflow.company_id && file.project_id === props.workflow.project_id)
  const filteredFiles = createMemo(() => files().filter((file) => file.filename.toLowerCase().includes(state.filter.trim().toLowerCase())))
  const selectedFile = createMemo(() => filteredFiles().find((file) => file.id === state.selectedFile) ?? filteredFiles()[0])
  const mutate = async (operation: () => Promise<unknown>) => {
    const key = identity()
    setState("mutationError", false)
    try {
      await operation()
      if (!disposed && key === identity()) await actions.refetch()
    } catch (error) {
      if (!disposed && key === identity()) setState("mutationError", true)
      throw error
    }
  }
  const action = (operation: () => Promise<unknown>) => mutate(operation).catch(() => undefined)
  return (
    <div class="flex h-full min-h-0 flex-col overflow-hidden rounded-xl border border-v2-border-border-base bg-v2-background-bg-base">
      <div class="flex shrink-0 items-center justify-end border-b border-v2-border-border-base px-3 py-2">
        <button type="button" class="rounded-md px-3 py-1 text-[12px] hover:bg-v2-overlay-simple-overlay-hover disabled:opacity-50"
          disabled={!hasScope() || data.loading} onClick={refresh}>{language.t("cowork.enterprise.workflows.data.refresh")}</button>
      </div>
      <Show when={state.mutationError}><p role="alert" class="p-3 text-[12px] text-v2-state-fg-danger">{language.t("cowork.enterprise.workflows.data.updateFailed")}</p></Show>
      <Show when={!props.loading && (!data.loading || currentData())} fallback={<p role="status" class="m-auto p-6 text-[13px]">{language.t("common.loading")}</p>}>
        <Show when={!props.error && !data.error} fallback={
          <p role="alert" class="m-auto p-6 text-[13px]">{language.t("cowork.enterprise.workflows.data.loadFailed")}</p>
        }>
          <ErrorBoundary fallback={(error, reset) => (
            <div role="alert" class="p-6 text-[13px]">
              <p>{language.t("cowork.enterprise.workflows.data.loadFailed")}</p>
              <button type="button" class="mt-3 underline" onClick={() => { reset(); refresh() }}>{language.t("cowork.enterprise.workflows.reporting.retry")}</button>
            </div>
          )}>
            <Suspense fallback={<p role="status" class="m-auto p-6">{language.t("common.loading")}</p>}>
              <Show when={props.tab === "files"}>
                <FilesTab workflow={props.workflow.name} filter={state.filter} setFilter={(value) => setState("filter", value)}
                  files={filteredFiles()} totalFiles={files().length} activeFile={selectedFile()} periodID={props.run ? props.run.period_id : props.periodID}
                  selectFile={(id) => setState("selectedFile", id)} hasContext
                  onDelete={(file) => action(async () => { await deleteFile(file.project_id, file.id, file.company_id); await props.onFilesChanged?.() })} />
              </Show>
              <Show when={props.tab !== "files"}>
                <Show when={hasScope()} fallback={<p class="m-auto p-6 text-[13px] text-v2-text-text-muted">{language.t("cowork.enterprise.workflows.data.noRun")}</p>}>
                  <Show when={props.tab === "extractions"}><RecordsTab title={language.t("cowork.pipeline.extractions")} records={currentData()?.extractions ?? []} kind="extraction" files={files()} /></Show>
                  <Show when={props.tab === "transformations"}><RecordsTab title={language.t("cowork.pipeline.transformations")} records={currentData()?.transformations ?? []} kind="transformation" /></Show>
                  <Show when={props.tab === "output"}><OutputTab transformations={currentData()?.transformations ?? []} artifacts={currentData()?.artifacts ?? []} /></Show>
                  <Show when={props.tab === "report"}><ReportTab artifacts={currentData()?.artifacts ?? []} loading={data.loading && !currentData()} onRefresh={refresh}
                    onDelete={(artifact) => action(() => deleteArtifact(artifact.project_id, artifact.id, artifact.company_id))} /></Show>
                  <Show when={props.tab === "approval"}><ApprovalTab approvals={currentData()?.approvals ?? []} artifacts={currentData()?.artifacts ?? []}
                    onRequest={(id, emails, message) => mutate(() => requestApprovals(props.workflow.project_id, id, emails, message, props.workflow.company_id))}
                    onUpdate={(id, email) => mutate(() => updateApprovalReviewer(props.workflow.project_id, id, email, props.workflow.company_id))}
                    onDelete={(id) => mutate(() => deleteApprovalReviewer(props.workflow.project_id, id, props.workflow.company_id))} /></Show>
                </Show>
              </Show>
            </Suspense>
          </ErrorBoundary>
        </Show>
      </Show>
    </div>
  )
}
